import React, { useState } from "react";
import SuperLayout from "../layout/SuperAdminLayout";
import { useAuth } from "../../context/AuthContext";
import { AdminUpdateProfile, addUser } from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const AdminProfilePanel = () => {
  const { user } = useAuth();

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [useFullName, setUseFullName] = useState(true);

  const [updateForm, setUpdateForm] = useState({
    firstName: user?.first_name || "",
    lastName: user?.last_name || "",
    displayName: user?.username || "",
    phone: user?.phone_number || "",
    dob: user?.kyc?.dob || "",
    address: user?.kyc?.address || "",
  });

  const [addUserForm, setAddUserForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "USER",
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const getInitials = (first, last) => {
    const f = first?.charAt(0) || "";
    const l = last?.charAt(0) || "";
    return (f + l).toUpperCase() || "SA";
  };

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setUpdateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddUserChange = (e) => {
    const { name, value } = e.target;
    setAddUserForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      await AdminUpdateProfile({
        first_name: updateForm.firstName,
        last_name: updateForm.lastName,
        username: useFullName 
          ? `${updateForm.firstName} ${updateForm.lastName}`.trim() 
          : updateForm.displayName,
        phone_number: updateForm.phone,
        dob: updateForm.dob,
        address: updateForm.address,
      });
      alert("Profile updated successfully");
      setIsUpdateModalOpen(false);
    } catch (err) {
      setErrorMsg(err.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    setLoading(true);
    setErrorMsg("");
    if (!addUserForm.name || !addUserForm.email || !addUserForm.password) {
      setErrorMsg("Name, Email and Password are required");
      setLoading(false);
      return;
    }
    try {
      await addUser(addUserForm);
      alert("User added successfully");
      setIsAddUserModalOpen(false);
      setAddUserForm({
        name: "",
        email: "",
        phone: "",
        password: "",
        role: "USER",
      });
    } catch (err) {
      setErrorMsg(err.message || "Failed to add user");
    } finally {
      setLoading(false);
    }
  };

  const profileData = {
    fullName: `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Not set",
    username: user?.username || "Not set",
    email: user?.email || "Not set",
    phone: user?.phone_number || "Not added yet",
    dob: user?.kyc?.dob || "Not added yet",
    address: user?.kyc?.address || "Not added yet",
    hospitalNumber: user?.kyc?.hospital_number || "—",
    walletBalance: user?.wallet_balance || "0.00",
    kycStatus: user?.kyc?.status?.toUpperCase() || "UNKNOWN",
    userId: user?.id || "—",
    lastLogin: user?.last_transactions?.[0]?.created_at
      ? new Date(user.last_transactions[0].created_at).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "Not available",
    registeredAt: user?.created_at
      ? new Date(user.created_at).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "Not available",
  };

  return (
    <SuperLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
            <p className="text-gray-600 mt-1">
              Manage your account information and settings
            </p>
          </div>
          <Button
            onClick={() => setIsAddUserModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Add User
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl shadow-sm p-8 flex flex-col items-center">
            <div className="w-48 h-48 rounded-full bg-blue-100 flex items-center justify-center text-6xl font-bold text-blue-600 border-4 border-blue-200">
              {getInitials(user?.first_name, user?.last_name)}
            </div>

            <span className="mt-6 inline-block bg-green-100 text-green-800 text-xs font-semibold px-4 py-1 rounded-full">
              {user?.role?.toUpperCase() || "ADMIN"}
            </span>

            <h2 className="mt-4 text-2xl font-bold text-gray-900">{profileData.fullName}</h2>
            <p className="text-gray-500 text-sm">{profileData.email}</p>

            <Button
              onClick={() => setIsUpdateModalOpen(true)}
              className="mt-8 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Update Profile
            </Button>
          </div>

          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-xl shadow-sm">
              <div className="px-8 py-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
                <p className="text-sm text-gray-600 mt-1">Basic info, like your name and contact details.</p>
              </div>
              <div className="p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Full Name</Label>
                    <div className="mt-2 text-gray-900">{profileData.fullName}</div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Username</Label>
                    <div className="mt-2 text-gray-900">{profileData.username}</div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Email</Label>
                    <div className="mt-2 text-gray-900">{profileData.email}</div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Phone Number</Label>
                    <div className="mt-2 text-gray-900">{profileData.phone}</div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Hospital Number</Label>
                    <div className="mt-2 text-gray-900">{profileData.hospitalNumber}</div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Wallet Balance</Label>
                    <div className="mt-2 text-xl font-bold text-green-700">
                      ₦{Number(profileData.walletBalance).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm">
              <div className="px-8 py-6">
                <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-6 text-center">
                  <div>
                    <p className="text-sm text-gray-600">KYC Status</p>
                    <p className="font-semibold text-green-700 mt-1">{profileData.kycStatus}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">User ID</p>
                    <p className="font-semibold text-gray-900 mt-1">{profileData.userId}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Last Login</p>
                    <p className="font-semibold text-gray-900 mt-1">{profileData.lastLogin}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Registered At</p>
                    <p className="font-semibold text-gray-900 mt-1">{profileData.registeredAt}</p>
                  </div>
                </div>
              </div>
            </div>

            {user?.last_transactions?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm">
                <div className="px-8 py-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Transactions</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {user.last_transactions.slice(0, 3).map((tx) => (
                      <div key={tx.id} className="flex justify-between items-center border-b pb-3">
                        <div>
                          <p className="font-medium">{tx.description}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(tx.created_at).toLocaleString()}
                          </p>
                        </div>
                        <p className={`font-bold ${tx.transaction_type === "debit" ? "text-red-600" : "text-green-600"}`}>
                          {tx.transaction_type === "debit" ? "-" : "+"}
                          ₦{Number(tx.amount).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <Dialog open={isUpdateModalOpen} onOpenChange={setIsUpdateModalOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Update Profile</DialogTitle>
            </DialogHeader>

            <div className="flex border-b">
              <button
                onClick={() => setActiveTab("personal")}
                className={`px-6 py-3 font-medium text-sm ${
                  activeTab === "personal"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Personal Info
              </button>
              <button
                onClick={() => setActiveTab("address")}
                className={`px-6 py-3 font-medium text-sm ${
                  activeTab === "address"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Address
              </button>
            </div>

            <div className="p-6">
              {activeTab === "personal" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={updateForm.firstName}
                      onChange={handleUpdateChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={updateForm.lastName}
                      onChange={handleUpdateChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="displayName">Display Name / Username *</Label>
                    <Input
                      id="displayName"
                      name="displayName"
                      value={updateForm.displayName}
                      onChange={handleUpdateChange}
                      disabled={useFullName}
                      className="mt-1 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={updateForm.phone}
                      onChange={handleUpdateChange}
                      className="mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="dob">Date of Birth</Label>
                    <Input
                      id="dob"
                      name="dob"
                      type="date"
                      value={updateForm.dob}
                      onChange={handleUpdateChange}
                      className="mt-1"
                    />
                  </div>
                  <div className="md:col-span-2 flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="useFullName"
                      checked={useFullName}
                      onChange={() => setUseFullName(!useFullName)}
                      className="h-5 w-5 text-blue-600 rounded"
                    />
                    <Label htmlFor="useFullName" className="text-sm text-gray-700">
                      Use Full Name as Display Name
                    </Label>
                  </div>
                </div>
              )}

              {activeTab === "address" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      name="address"
                      value={updateForm.address}
                      onChange={handleUpdateChange}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsUpdateModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateProfile} disabled={loading}>
                {loading ? "Updating..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isAddUserModalOpen} onOpenChange={setIsAddUserModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={addUserForm.name}
                  onChange={handleAddUserChange}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={addUserForm.email}
                  onChange={handleAddUserChange}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={addUserForm.phone}
                  onChange={handleAddUserChange}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={addUserForm.password}
                  onChange={handleAddUserChange}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  value={addUserForm.role}
                  onValueChange={(val) => setAddUserForm((prev) => ({ ...prev, role: val }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USER">User</SelectItem>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {errorMsg && <p className="text-red-600 text-sm mt-2">{errorMsg}</p>}

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddUserModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddUser} disabled={loading}>
                {loading ? "Adding..." : "Add User"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SuperLayout>
  );
};

export default AdminProfilePanel;